import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { environment } from 'environments/environment';
import { StudentBookService } from 'modules/foc/services/student-book.service';

@Injectable()
export class ResourceBankHelperService {

  private readonly userRole: string;
  private readonly userData: any;
  private bundleProducts: any;
  private spaceId: string;
  externalLinksBaseURL: string = environment.externalLinksBaseURL;

  constructor(
    private readonly authService: AuthService,
    private readonly studentBookService: StudentBookService
  ) {
    this.userData = this.authService.getUserData();
    this.userRole = this.userData.role === "student" ? "student" : "teacher";
  }

  showResourceBankButton(bundleProducts?, spaceId?) {
    if (bundleProducts) this.bundleProducts = bundleProducts;
    if (spaceId) this.spaceId = spaceId;

    if (this.userRole == 'student' || this.userData.launchType == 'lti' || !this.bundleProducts?.length) {
      return false;
    }

    return this.getResourceBankProducts().length > 0;
  }

  getResourceBankProducts() {
    return this.bundleProducts.filter(product => this.isResourceBankType(product) && this.studentBookService.isProductActive(product) && this.isResourceBankVisible(product));
  }

  navigateToResourceBank(bundleCode: string, spaceId: string, classId?: string): void {
    const resourceBankProducts = this.getResourceBankProducts();
    const singleProduct = resourceBankProducts.length === 1;
    const resourceBankProduct = singleProduct ? resourceBankProducts[0] : null;
    const productName = resourceBankProduct?.['dls-product']?.code;
    const isComingSoon = resourceBankProduct?.meta?.visibility === 'coming-soon';

    let url = this.externalLinksBaseURL;

    if (singleProduct && isComingSoon) {
      // If there is only one product and it is coming soon, we need to navigate to the coming soon page
      url += classId
        ? `dashboard/${spaceId}/class/${classId}/bundle/${bundleCode}/product/${productName}/coming-soon`
        : `dashboard/${spaceId}/bundle/${bundleCode}/product/${productName}/coming-soon`;
    } else if (singleProduct) {
      // If there is only one product, navigate to the resource bank directly
      url += `dashboard/teacher/${spaceId}/resourcebank/${productName}/${classId || ''}`;
    } else if (classId) {
      // If there are multiple products and ebook is launched from class, 
      // Case 1 : if all the products have parent tile set to 'resources', then navigate to the Resources folder inside the class
      // Case 2 : else, just navigate to the Class details page
      url += `class/teacher/${spaceId}/class/${classId}/view/materials?bundleCode=${bundleCode}`;
      if (resourceBankProducts.every(product => product.meta?.category?.id === 'resources')) {
        url += '&launchResourceBank=true';
      }
    } else {
      // If there are multiple products and ebook is launched from library, navigate to the Bundle details page
      url += `dashboard/teacher/${spaceId}/bundle/${bundleCode}/view`;
    }

    window.open(url, '_blank');
  }

  resetBundleProducts() {
    this.bundleProducts = undefined;
  }

  isResourceBankVisible(product) {
    // If the product does not have a visibility meta property or if the spaceId is 'mqa', we consider it visible
    // This is to ensure that products in the MQA space are always visible
    if (!product.meta?.visibility || this.spaceId.indexOf("mqa") === 4) {
      return true;
    }

    const visibleStates = ['true', 'coming-soon'];
    return visibleStates.includes(product.meta.visibility);
  }

  /**
   * Resource bank products are identified by the following criteria:
   * - The product's 'ext-product' type is 'OLP'
   * - The product's 'learning-path' version is '3' or the meta property 'learning-path' is '3'
   * @param product bundle product object
   * @returns true if the product is a Resource Bank type, false otherwise
   */
  isResourceBankType(product) {
    return product['ext-product']?.type == 'OLP' &&
      ((product['learning-path']?.version ||
        product.meta['learning-path']) == '3')
  }
}